/**
 * Preorders Query Hooks
 * 封裝預購相關的查詢 hooks（使用 SWR）
 */

import {
  usePreordersGetActive,
  usePreordersGetActiveCampaigns,
  type PreordersGetActiveCampaigns200DataItem,
} from '@pos/api-sdk';
import { getApiHeaders, getTenantIdFromUrl } from '../../api/apiUtils';
import { useTenant } from '../../contexts/TenantContext';
import { swrDetailConfig } from '../../lib/swr-config';
import type { SWRConfiguration } from 'swr';

/** 可預購檔期列表（由 SDK PreordersGetActiveCampaigns200DataItem 匯出） */
export type ActivePreorderCampaign = PreordersGetActiveCampaigns200DataItem;

/**
 * 取得活躍的預購檔期
 */
export function usePreorderCampaign(options?: {
  fetch?: RequestInit;
  revalidateOnFocus?: boolean;
  dedupingInterval?: number;
  enabled?: boolean;
  swr?: SWRConfiguration;
}) {
  // 優先使用 TenantContext 的 tenantId，如果沒有則從 URL 取得
  const { tenantId: contextTenantId } = useTenant();
  const urlTenantId = getTenantIdFromUrl();
  const tenantId = contextTenantId || urlTenantId;

  const {
    revalidateOnFocus = true,
    dedupingInterval = 1000,
    enabled = true,
    swr: swrOptions,
  } = options || {};

  // 取得 headers 並確保正確傳遞
  const apiHeaders = getApiHeaders(tenantId);

  // 除錯：確認 headers 內容
  if (tenantId) {
    console.log('[usePreorderCampaign] 準備發送請求，headers:', apiHeaders);
  }

  // 正確合併 headers：先展開其他 fetch 選項，然後明確設定 headers
  const fetchOptions: RequestInit = {
    ...options?.fetch,
    headers: {
      ...apiHeaders,
      ...(options?.fetch?.headers || {}),
    },
  };

  const result = usePreordersGetActive({
    fetch: fetchOptions,
    swr: {
      ...swrDetailConfig,
      enabled: enabled && !!tenantId,
      revalidateOnFocus,
      dedupingInterval,
      ...swrOptions,
    },
  });

  // 提取實際資料
  const campaignData = result.data?.data;
  const campaign =
    campaignData && 'data' in campaignData ? campaignData.data : undefined;

  return {
    campaign,
    isLoading: result.isLoading,
    isError: !!result.error,
    error: result.error,
    mutate: result.mutate,
    isValidating: result.isValidating,
  };
}

/**
 * 取得可預購檔期列表（兩個以上活動時先顯示活動列表用）
 * 使用 SDK 產生的 usePreordersGetActiveCampaigns
 */
export function usePreorderCampaigns(options?: {
  revalidateOnFocus?: boolean;
  dedupingInterval?: number;
  enabled?: boolean;
  swr?: SWRConfiguration;
}) {
  const { tenantId: contextTenantId } = useTenant();
  const urlTenantId = getTenantIdFromUrl();
  const tenantId = contextTenantId || urlTenantId;

  const {
    revalidateOnFocus = true,
    dedupingInterval = 1000,
    enabled = true,
    swr: swrOptions,
  } = options || {};

  const fetchOptions: RequestInit = {
    headers: getApiHeaders(tenantId ?? ''),
  };

  const result = usePreordersGetActiveCampaigns({
    fetch: fetchOptions,
    swr: {
      ...swrDetailConfig,
      enabled: enabled && !!tenantId,
      revalidateOnFocus,
      dedupingInterval,
      ...swrOptions,
    },
  });

  const responseData = result.data?.data;
  const campaigns: ActivePreorderCampaign[] | undefined =
    responseData && 'data' in responseData && Array.isArray(responseData.data)
      ? responseData.data
      : undefined;

  return {
    campaigns,
    isLoading: result.isLoading,
    isError: !!result.error,
    error: result.error,
    mutate: result.mutate,
    isValidating: result.isValidating,
  };
}
